/* eslint-disable @atlassian/i18n/no-literal-string-in-jsx */
import { token } from '@atlaskit/tokens';
import { GlobalPageLoadExperience } from '@atlassian/ufo';
import FieldNameContainer from '@databases/field-name/FieldNameContainer';
import {
  FieldNameInlineContainer,
  FieldNameWithActionsContainer
} from '@databases/field-name/FieldNameWithActions';
import { useMarkMountTimings } from '@databases/frontend-metrics/useMarkMountTimings';
import { SkeletonContext, SkeletonItem } from '@databases/skeleton';
import { DatabaseValueViewContainer } from '@databases/styles';
import {
  IGNORED_TABLE_ELEMENT_CLASS,
  TableResizerContainer
} from '@databases/table-resizer';
import { DatabaseLayoutType } from '@databases/types/database/view';
import { TABLE_CLASSNAMES } from '@databases/utils/constants';
import { StyledInlineEditWrapperWithoutComponents } from '@databases/value/DatabaseValueInlineEdit';
import classNames from 'classnames';
import { range } from 'lodash-es';
import React, { type FunctionComponent } from 'react';
import styled from 'styled-components';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';

import { DatabaseBoardSkeleton } from '../database-skeletons/DatabaseBoardSkeleton.tsx';
import { DatabaseGallerySkeleton } from '../database-skeletons/DatabaseGallerySkeleton.tsx';
import { DatabaseTableSkeletonRefresh } from '../database-skeletons/DatabaseTableSkeletonRefresh.tsx';
import { DatabaseToolbarSkeleton } from '../database-skeletons/DatabaseToolbarSkeleton.tsx';

import DatabaseContentWrapper from './DatabaseContentWrapper.ts';
import { Table, TableLayoutWrapper } from './table-layout/Table.tsx';
import { Th } from './table-layout/TableLayoutHeaderCell.tsx';
import { Tr } from './table-layout/TableLayoutRow.tsx';

const DatabaseContentContainer = styled.div`
  position: relative;
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  min-height: 0;
`;

// overflow: visible below is to hide scrollbars
// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
const DatabaseContentSkeletonWrapper = styled(DatabaseContentContainer)`
  * {
    user-select: none;
    border: none;
    outline: none;
    cursor: default;
  }

  > ${DatabaseContentWrapper} > div {
    overflow: visible;
  }

  &&& .${TABLE_CLASSNAMES.ADD_FIELD} {
    right: 50px;
  }

  .add-entry {
    grid-column: 1 / -1;
    padding: 10px ${token('space.100', '8px')};
  }
`;

export const DatabaseTableSkeleton: FunctionComponent = () => {
  const visualRefreshIcons = isFeatureFlagEnabled('platform-visual-refresh-icons');

  return (
    <SkeletonContext isEnabled={true}>
      <DatabaseContentSkeletonWrapper>
        <DatabaseContentWrapper hasCalulations={false}>
          <TableLayoutWrapper containerWidth="100%">
            <TableResizerContainer>
              <Table
                gridTemplateColumns="1fr 1fr 1fr 1fr 1px"
                minWidth={624}
                actionsMidWidth={1}
                containerHeight={600}
              >
                <thead>
                  <tr>
                    {range(4).map((i) => (
                      <Th isDragOver={false} containerHeight={0} key={i}>
                        <FieldNameWithActionsContainer>
                          <FieldNameInlineContainer
                            visualRefreshIcons={visualRefreshIcons}
                          >
                            <FieldNameContainer>
                              <SkeletonItem hasIcon minWidth={80}>
                                Field Name
                              </SkeletonItem>
                            </FieldNameContainer>
                          </FieldNameInlineContainer>
                        </FieldNameWithActionsContainer>
                      </Th>
                    ))}
                    <th
                      className={classNames([TABLE_CLASSNAMES.ADD_FIELD], {
                        [IGNORED_TABLE_ELEMENT_CLASS]: true
                      })}
                    >
                      <SkeletonItem isButton height={20} width={20}></SkeletonItem>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {range(4).map((i) => {
                    const content =
                      i % 2 === 0 ? 'xxxxxxxxxxxxxxxxxxx' : 'xxxxxxxxxxxxxxx';

                    return (
                      <Tr
                        isInitialized
                        isFilterOrSortingActive={false}
                        key={i}
                        insertIndicatorPosition={undefined}
                      >
                        {range(4).map((i) => (
                          <th key={i}>
                            <StyledInlineEditWrapperWithoutComponents>
                              <DatabaseValueViewContainer>
                                <SkeletonItem>{content}</SkeletonItem>
                              </DatabaseValueViewContainer>
                            </StyledInlineEditWrapperWithoutComponents>
                          </th>
                        ))}
                        <td
                          className={classNames([TABLE_CLASSNAMES.ACTIONS], {
                            [IGNORED_TABLE_ELEMENT_CLASS]: true
                          })}
                        />
                      </Tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="add-entry">
                      <SkeletonItem hasIcon maxWidth={80}>
                        Add entry
                      </SkeletonItem>
                    </td>
                  </tr>
                </tfoot>
              </Table>
            </TableResizerContainer>
          </TableLayoutWrapper>
        </DatabaseContentWrapper>
      </DatabaseContentSkeletonWrapper>
    </SkeletonContext>
  );
};

type DatabaseContentSkeletonProps = {
  layoutType: DatabaseLayoutType;
};

const DatabaseContentSkeleton: FunctionComponent<DatabaseContentSkeletonProps> = ({
  layoutType
}: DatabaseContentSkeletonProps) => {
  useMarkMountTimings({
    onStart: () => GlobalPageLoadExperience.mark('mount-skeleton-start'),
    onMounted: () => GlobalPageLoadExperience.mark('mount-skeleton-end')
  });

  const renderSkeletonContent = () => {
    switch (layoutType) {
      case DatabaseLayoutType.BOARD:
        return <DatabaseBoardSkeleton />;
      case DatabaseLayoutType.GALLERY:
        return <DatabaseGallerySkeleton />;
      default:
        return <DatabaseTableSkeletonRefresh />;
    }
  };

  return (
    <SkeletonContext isEnabled={true}>
      <DatabaseContentSkeletonWrapper>
        <DatabaseToolbarSkeleton />
        {renderSkeletonContent()}
      </DatabaseContentSkeletonWrapper>
    </SkeletonContext>
  );
};

export default DatabaseContentSkeleton;
